import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../api/axios'
import { formatImageUrl } from '../utils/formatImageUrl'

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
        return new Date(dateStr).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch { return dateStr; }
};

const getImage = (post) => {
    return formatImageUrl(post?.image, '/assets/images/thumbs/blog-img1.png');
};

const BlogCard = ({ post }) => {
    const slug = post.slug || `blog-${post.id}`;
    return (
        <div className="blog-story-card h-100 d-flex flex-column border border-gray-100 rounded-16 overflow-hidden bg-white">
            <Link
                to={`/blog/${slug}`}
                state={{ post: { ...post, image: getImage(post), date: formatDate(post.date || post.createdAt), readTime: post.readTime || '4 min read', category: post.category || 'Healthy Living' }, allPosts: [] }}
                className="d-block position-relative w-100 overflow-hidden"
                style={{ aspectRatio: '16 / 10' }}
            >
                <img src={getImage(post)} alt={post.title} className="cover-img" />
                <span className="bg-main-600 text-white text-xs fw-semibold py-4 px-12 rounded-8 position-absolute" style={{ top: 12, left: 12, letterSpacing: '0.5px' }}>
                    BLOG
                </span>
            </Link>

            <div className="p-20 d-flex flex-column flex-grow-1">
                <div className="flex-align flex-wrap gap-8 mb-8">
                    <span className="text-lg text-main-600"><i className="ph ph-calendar-dots" /></span>
                    <span className="text-sm text-gray-500">{formatDate(post.date || post.createdAt)}</span>
                    {post.category && (
                        <span className="bg-main-50 text-main-600 py-2 px-12 rounded-8 text-xs fw-semibold ms-auto">{post.category}</span>
                    )}
                </div>

                <h6 className="text-lg mb-8">
                    <Link to={`/blog/${slug}`} state={{ post: { ...post, image: getImage(post), date: formatDate(post.date || post.createdAt), readTime: '4 min read', category: post.category || 'Healthy Living' }, allPosts: [] }} className="text-neutral-700 hover-text-main-600 text-line-2">
                        {post.title}
                    </Link>
                </h6>

                <p className="text-gray-700 text-sm text-line-3 mb-16">{post.excerpt || post.description || ''}</p>

                <Link
                    to={`/blog/${slug}`}
                    state={{ post: { ...post, image: getImage(post), date: formatDate(post.date || post.createdAt), readTime: '4 min read', category: post.category || 'Healthy Living' }, allPosts: [] }}
                    className="text-main-600 fw-semibold flex-align gap-8 hover-text-main-700 mt-auto"
                >
                    Read Blog Post
                    <i className="ph ph-arrow-right" />
                </Link>
            </div>
        </div>
    )
}

// Fallback posts shown while loading or on API error
const fallbackPosts = [
    { id: 1, image: '/assets/images/thumbs/blog-img1.png', category: 'Natural Foods', date: 'August 7, 2026', title: 'Why Choosing Natural Ingredients Matters for Your Family', excerpt: 'From the kitchen to the dinner table, small ingredient choices add up. Here is why natural, minimally processed foods deserve a place in your everyday cooking.', slug: 'natural-ingredients' },
    { id: 2, image: '/assets/images/thumbs/blog-img2.png', category: 'Family Wellness', date: 'August 5, 2026', title: 'Simple Ways to Build Healthier Eating Habits at Home', excerpt: 'Healthier eating does not need a complete overhaul. These small, sustainable habits make it easier for the whole family to eat well together.', slug: 'healthy-eating-habits' },
    { id: 3, image: '/assets/images/thumbs/blog-img3.png', category: 'Healthy Living', date: 'August 3, 2026', title: 'Understanding the Benefits of Traditional Indian Foods', excerpt: 'Long before packaged food existed, Indian households relied on time-tested ingredients and methods. We look at why they still matter today.', slug: 'traditional-indian-foods' },
    { id: 4, image: '/assets/images/thumbs/blog-img4.png', category: 'Healthy Recipes', date: 'August 2, 2026', title: 'Healthy Snacks Your Family Will Love', excerpt: 'Skip the processed snack aisle. These easy, wholesome recipes are quick to make and loved by kids and adults alike.', slug: 'healthy-snacks' },
];

const Blog = () => {
    const [blogPosts, setBlogPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBlogPosts = async () => {
            try {
                const res = await api.get('/api/blogs');
                if (res.data && res.data.length > 0) {
                    setBlogPosts(res.data);
                } else {
                    setBlogPosts(fallbackPosts);
                }
            } catch (err) {
                console.error('Failed to fetch blogs', err);
                setBlogPosts(fallbackPosts);
            }
            setLoading(false);
        };
        fetchBlogPosts();
    }, []);

    return (
        <section className="blog py-80">
            <div className="container container-lg">
                <div className="text-center mb-56" style={{ maxWidth: 640, margin: '0 auto' }}>
                    <h2 className="mb-16">
                        Our <span className="text-main-600">Blog</span> &amp; Stories
                    </h2>
                    <p className="text-gray-700">
                        Nourishing reads on natural ingredients, family wellness, and mindful,
                        everyday living — straight from the Healthy Home kitchen.
                    </p>
                </div>

                {loading ? (
                    <div className="text-center py-40">
                        <p className="text-gray-500">Loading blog posts...</p>
                    </div>
                ) : (
                    <div className="row g-4">
                        {blogPosts.map((post) => (
                            <div className="col-xl-3 col-lg-4 col-md-6 col-sm-6" key={post.id}>
                                <BlogCard post={post} />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    )
}

export default Blog